import React from "react";

const CARDS = [
  {
    title: "ImageIQ",
    desc: "Turn article images to actionable touchpoints",
    iconWrap: "bg-amber-100",
    iconColor: "text-amber-600",
    Icon: (props) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        {...props}
      >
        <rect x="4" y="5" width="16" height="14" rx="2" />
        <path d="M9 10.5h.01M12 12l3 4H9l3-4z" />
      </svg>
    ),
  },
  {
    title: "VideoIQ",
    desc: "Interactive video widget for measurable views",
    iconWrap: "bg-emerald-100",
    iconColor: "text-emerald-600",
    Icon: (props) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        {...props}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.5 10.5l4-2v7l-4-2M5 8h7a2 2 0 012 2v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4a2-2 0 012-2z"
        />
      </svg>
    ),
  },
  {
    title: "StoryIQ",
    desc: "Carousel turning text into compact article snippets",
    iconWrap: "bg-sky-100",
    iconColor: "text-sky-600",
    Icon: (props) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        {...props}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13 3L4 14h6l-1 7 9-11h-6l1-7z"
        />
      </svg>
    ),
  },
  {
    title: "PulseIQ",
    desc: "Turn lost archive into fresh engagement, contextually",
    iconWrap: "bg-emerald-100",
    iconColor: "text-emerald-600",
    Icon: (props) => (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        {...props}
      >
        <path d="M1.5 12S5 5.5 12 5.5 22.5 12 22.5 12 19 18.5 12 18.5 1.5 12 1.5 12Z" />
        <circle cx="12" cy="12" r="3.2" />
      </svg>
    ),
  },
];

function Card({ title, desc, Icon, iconWrap, iconColor }) {
  return (
    <div className="rounded-2xl border-2 border-[#06382f] bg-white ring-1 ring-black/5 px-3 text-center flex flex-col items-center justify-center gap-2  h-[140px] max-w-[190px] md:h-[170px] md:max-w-[220px]  w-full mx-auto">
      <div className={`rounded-full ${iconWrap} p-2`}>
        <Icon className={`w-6 h-6 ${iconColor}`} />
      </div>
      <div className="text-[14px] md:text-[16px] lg:text-[18px] font-bold font-inter text-[#06382f] leading-[100%]">
        {title}
      </div>
      <div className="text-[12px] md:text-[13px] lg:text-sm leading-[120%] font-normal text-[#004D4D] font-inter">
        {desc}
      </div>
    </div>
  );
}

export default function StoryLensIntro() {
  return (
    <section className="relative overflow-hidden w-full max-w-4xl mx-auto">
      {/* Headings */}
      <div className="text-center">
        <div className="font-bebas font-normal tracking-wider text-[30px]  lg:text-[50px] text-center leading-[130%] text-[#06382f]">
          Introducing <span className="text-[#0E8A43]">StoryLens</span> <br />
          <span className="text-[28px] sm:text-[30px] lg:text-[50px]">
            The Unified Engagement Suite
          </span>
        </div>

        <div className="hidden mt-4 text-[12px] text-center text-[#004D4D] sm:text-sm lg:text-lg font-inter font-normal leading-[100%]">
          Designed to increase both engagement and monetization
        </div>
      </div>

      {/* ======= Diagram ======= */}
      <div className="relative mt-8 max-w-[407px] md:max-w-none mx-auto">
        {/* Top node */}
        <div className="mx-auto w-fit rounded-2xl border-2 border-[#06382f] bg-white p-3 text-slate-800 font-semibold text-[14px] md:text-[16px] lg:text-[18px]">
          Content Engagement
        </div>

        {/* Desktop connectors */}
        <div className="relative hidden md:block">
          <div className="mx-auto h-8 w-[2px] bg-[#06382f]"></div>
          <div className="mx-auto w-[88%] h-[2px] bg-[#06382f]"></div>
        </div>

        {/* Mobile layout */}
        <div className="md:hidden relative mt-6">
          <div className="absolute left-1/2 -translate-x-1/2 -top-6 h-[582px] max-w-4xl w-[2px] bg-[#06382f]" />

          {/* ROW 1 */}
          <div className="relative grid grid-cols-2 gap-x-6 gap-y-0">
            <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 h-[2px] w-6 bg-[#06382f] rounded" />
            <Card {...CARDS[0]} />
            <Card {...CARDS[1]} />
          </div>

          <div className="h-6" />

          {/* ROW 2 */}
          <div className="relative grid grid-cols-2 gap-x-6">
            <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 h-[2px] w-6 bg-[#06382f] rounded" />
            <Card {...CARDS[2]} />
            <Card {...CARDS[3]} />
          </div>
        </div>

        {/* Desktop 4 cards */}
        <div className="hidden md:grid mt-6 grid-cols-4 gap-6">
          {CARDS.map((c) => (
            <div key={c.title} className="relative">
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-[2px] h-6 bg-[#06382f]" />
              <Card {...c} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
